import { Component, Input, OnInit } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Datastore, startWithEmptyList } from '@freelancer/datastore';
import {
  BillingAgreementsCollection,
  CurrenciesIncludingExternalCollection,
  PaymentShareMembersCollection,
  PaymentShareMembersSpentAmountCollection,
  PaymentShareTeam,
  PaymentShareTeamsCollection,
  User,
  UsersCollection,
} from '@freelancer/datastore/collections';
import { ModalRef } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import { UsernameColor } from '@freelancer/ui/username';
import { toNumber } from '@freelancer/utils';
import {
  PaymentShareEntityTypeApi,
  PaymentShareMemberStatusApi,
} from 'api-typings/users/users';
import * as Rx from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PaymentShareMemberSpendingInfo } from './payment-share-member-modal.types';

@Component({
  selector: 'app-payment-share-member-modal',
  template: `
    <fl-bit *ngIf="isReady$ | flAsync; else loading">
      <fl-sticky-footer-wrapper flTrackingSection="PaymentShareMemberModal">
        <fl-sticky-footer-body>
          <fl-heading
            flTrackingSection="PaymentShareMemberModal"
            [flMarginBottom]="Margin.XXXSMALL"
            [headingType]="HeadingType.H2"
            [size]="TextSize.MID"
          >
            {{ (paymentShareTeam$ | flAsync)?.paymentShareTeamName }}
          </fl-heading>
          <fl-bit class="ModalSubHeader">
            <fl-text
              i18n="
                Payment sharing team owner's information of the team payment
              "
              [flMarginBottom]="Margin.LARGE"
            >
              Owned by&nbsp;
            </fl-text>
            <fl-username
              flTrackingLabel="SubtitleUserName"
              [username]="(owner$ | flAsync)?.username"
              [color]="UsernameColor.INHERIT"
              [link]="(owner$ | flAsync)?.profileUrl"
            ></fl-username>
          </fl-bit>
          <fl-text
            [flMarginBottom]="Margin.LARGE"
            i18n="Payment sharing team's information"
          >
            The payment method is owned by
            <strong>{{ (owner$ | flAsync)?.displayName }}</strong
            >(<fl-username
              class="UserName"
              flTrackingLabel="OwnerUserName1"
              [username]="(owner$ | flAsync)?.username"
              [color]="UsernameColor.INHERIT"
              [link]="(owner$ | flAsync)?.profileUrl"
            ></fl-username
            >) who has added you to their team. Payments you make using this
            method will be charged to
            <strong>{{ (owner$ | flAsync)?.displayName }}</strong
            >'s (<fl-username
              class="UserName"
              flTrackingLabel="OwnerUserName2"
              [username]="(owner$ | flAsync)?.username"
              [color]="UsernameColor.INHERIT"
              [link]="(owner$ | flAsync)?.profileUrl"
            ></fl-username
            >) payment method. They will be notified and can view all
            transactions made using this payment method.
          </fl-text>

          <app-payment-share-member-modal-member-table
            [memberSpendingInfo]="memberInfo$ | flAsync"
          >
          </app-payment-share-member-modal-member-table>
        </fl-sticky-footer-body>
        <fl-sticky-footer class="ActionContainer">
          <fl-button
            i18n="Payment sharing modal Close Button"
            flTrackingLabel="CloseButton"
            class="CTAButton"
            [color]="ButtonColor.TRANSPARENT_DARK"
            [size]="ButtonSize.SMALL"
            (click)="handleOk()"
          >
            Close
          </fl-button>
        </fl-sticky-footer>
      </fl-sticky-footer-wrapper>
    </fl-bit>
    <ng-template #loading>
      <fl-spinner
        flTrackingLabel="PaymentShareMemberModalSpinner"
        [overlay]="true"
      ></fl-spinner>
    </ng-template>
  `,
  styleUrls: ['./payment-share-member-modal.component.scss'],
})
export class PaymentShareMemberModalComponent implements OnInit {
  Margin = Margin;
  HeadingType = HeadingType;
  TextSize = TextSize;
  FontType = FontType;
  FontWeight = FontWeight;
  ButtonSize = ButtonSize;
  ButtonColor = ButtonColor;
  UsernameColor = UsernameColor;

  @Input() billingAgreementId: number;

  paymentShareTeam$: Rx.Observable<PaymentShareTeam>;
  owner$: Rx.Observable<User | undefined>;
  isReady$: Rx.Observable<boolean>;
  memberInfo$: Rx.Observable<readonly PaymentShareMemberSpendingInfo[]>;

  constructor(
    private modalRef: ModalRef<PaymentShareMemberModalComponent>,
    private auth: Auth,
    private datastore: Datastore,
  ) {}

  ngOnInit(): void {
    const memberCollection = this.datastore.collection<PaymentShareMembersCollection>(
      'paymentShareMembers',
      query =>
        query
          .where('billingAgreementId', '==', this.billingAgreementId)
          .where(
            'entityId',
            '==',
            this.auth.getUserId().pipe(map(userId => toNumber(userId))),
          )
          .where('entityType', '==', PaymentShareEntityTypeApi.USER)
          .where('status', '==', PaymentShareMemberStatusApi.ADDED),
    );

    const member$ = memberCollection.valueChanges().pipe(
      filter(members => members.length > 0),
      map(members => members[0]),
    );

    const teamId$ = member$.pipe(map(member => member.paymentShareTeamId));

    const memberId$ = member$.pipe(map(member => member.id));

    const memberSpentCollection = this.datastore.collection<PaymentShareMembersSpentAmountCollection>(
      'paymentShareMembersSpentAmount',
      query =>
        query
          .where('id', '==', memberId$)
          .where(
            'entityId',
            '==',
            this.auth.getUserId().pipe(map(userId => toNumber(userId))),
          )
          .where('entityType', '==', PaymentShareEntityTypeApi.USER),
    );

    const currentSpentAmount$ = memberSpentCollection.valueChanges().pipe(
      startWithEmptyList(),
      map(members =>
        members.length > 0 ? members[0].currentSpentAmount : undefined,
      ),
    );

    const paymentShareTeamsCollection = this.datastore.collection<PaymentShareTeamsCollection>(
      'paymentShareTeams',
      query => query.where('id', '==', teamId$),
    );

    this.paymentShareTeam$ = paymentShareTeamsCollection.valueChanges().pipe(
      filter(paymentTeams => paymentTeams.length > 0),
      map(paymentTeams => paymentTeams[0]),
    );

    const ownerUserId$ = this.paymentShareTeam$.pipe(
      filter(team => team.ownerEntityType === PaymentShareEntityTypeApi.USER),
      map(team => team.ownerEntityId),
    );

    this.owner$ = this.datastore
      .document<UsersCollection>('users', ownerUserId$)
      .valueChanges();

    const billingAgreementId$ = member$.pipe(
      map(member => member.billingAgreementId),
    );

    const billingAgreement$ = this.datastore
      .document<BillingAgreementsCollection>(
        'billingAgreements',
        billingAgreementId$,
      )
      .valueChanges();

    const currencies$ = this.datastore
      .collection<CurrenciesIncludingExternalCollection>(
        'currenciesIncludingExternal',
      )
      .valueChanges();

    const currency$ = Rx.combineLatest([billingAgreement$, currencies$]).pipe(
      map(([billingAgreement, currencies]) =>
        currencies.find(c => c.id === billingAgreement.currencyId),
      ),
    );

    this.isReady$ = Rx.combineLatest([this.paymentShareTeam$, member$]).pipe(
      map(([team, member]) => {
        // only a team member (not owner) can view the member modal
        if (
          team.ownerEntityId === member.entityId &&
          team.ownerEntityType === member.entityType
        ) {
          throw new Error(`Cannot show this component for the team owner`);
        }
        return true;
      }),
    );

    this.memberInfo$ = Rx.combineLatest([
      this.paymentShareTeam$,
      currentSpentAmount$,
      currency$,
    ]).pipe(
      map(([team, currentSpentAmount, currency]) => [
        {
          currency,
          isSpendingLimitActive: team.isSpendingLimitActive,
          spendingLimitAmount: team.memberSpendingLimitAmount,
          currentSpentAmount,
          spendingLimitDuration: team.memberSpendingLimitDuration,
        },
      ]),
    );
  }

  handleOk() {
    this.modalRef.close();
  }
}
